﻿import { subHours } from 'date-fns';
import { action, computed, makeObservable, observable } from 'mobx';
import { AccountMpnId, CompanyInformation, UserInfo, UserRole, RoleName } from '../models';
import { isPpeEnvironment } from '../configurations';

const UPDATE_USERINFO_INTERVAL_IN_HOURS = 8;

export class UserStore {
  @observable userInfo?: UserInfo;

  @observable userMpnIds: AccountMpnId[] = [];

  @observable companyInformation?: CompanyInformation;

  @observable accountHasAccess: boolean = false;

  @observable isShowPartnerPage: boolean = false;

  @observable roles: UserRole[] = [];

  @observable selectedRole: RoleName | undefined = undefined;

  @observable isUserSubjectToEORestrictions: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @action
  setAccountHasAccess(accountHasAccess: boolean) {
    this.accountHasAccess = accountHasAccess;
  }

  @action
  setIsShowPartnerPage(isShowPartnerPage: boolean) {
    this.isShowPartnerPage = isShowPartnerPage;
  }

  @action
  setIsUserSubjectToEORestrictions(isUserSubjectToEORestrictions: boolean): void {
    this.isUserSubjectToEORestrictions = isUserSubjectToEORestrictions;
  }

  @action
  setCurrentUser(userInfo: UserInfo): void {
    this.userInfo = userInfo;
    if (!userInfo.roles.includes(RoleName.Admin)) {
      this.isShowPartnerPage = true;
    }
  }

  @action
  setUserMpnIds(userMpnIds: AccountMpnId[]): void {
    this.userMpnIds = userMpnIds;
  }

  @action
  setCompanyInformation(companyInformation: CompanyInformation): void {
    this.companyInformation = companyInformation;
  }

  isAuthorized(allowedRoles: RoleName[]): boolean {
    const userRoles = this.userInfo?.roles;
    if (!userRoles) return false;
    return userRoles.some((userRole) => allowedRoles.includes(userRole));
  }

  @computed
  get isAdmin(): boolean {
    return (this.userInfo?.roles || []).includes(RoleName.Admin);
  }

  @computed
  get userId(): number | undefined {
    return this.userInfo?.userId;
  }

  @computed
  get isStaff(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return (
        [
          RoleName.GroupLead,
          RoleName.AwardLead,
          RoleName.AwardNominationReviewer,
          RoleName.RegionLead,
          RoleName.CountryLead,
          RoleName.CountryNominationReviewer,
          RoleName.Judge,
        ].some((role) => role === this.selectedRole) || (this.userInfo?.roles || []).includes(RoleName.Admin)
      );
    }
    return (
      (this.userInfo?.roles || []).includes(RoleName.Admin) ||
      (this.userInfo?.roles || []).includes(RoleName.GroupLead) ||
      (this.userInfo?.roles || []).includes(RoleName.AwardLead) ||
      (this.userInfo?.roles || []).includes(RoleName.AwardNominationReviewer) ||
      (this.userInfo?.roles || []).includes(RoleName.RegionLead) ||
      (this.userInfo?.roles || []).includes(RoleName.CountryLead) ||
      (this.userInfo?.roles || []).includes(RoleName.CountryNominationReviewer) ||
      (this.userInfo?.roles || []).includes(RoleName.Judge)
    );
  }

  @computed
  get isLead(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return [RoleName.GroupLead, RoleName.AwardLead, RoleName.RegionLead, RoleName.CountryLead].some((role) => role === this.selectedRole);
    }
    return (
      (this.userInfo?.roles || []).includes(RoleName.GroupLead) ||
      (this.userInfo?.roles || []).includes(RoleName.AwardLead) ||
      (this.userInfo?.roles || []).includes(RoleName.RegionLead) ||
      (this.userInfo?.roles || []).includes(RoleName.CountryLead)
    );
  }

  @computed
  get isReviewer(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return [RoleName.AwardNominationReviewer, RoleName.CountryNominationReviewer].some((role) => role === this.selectedRole);
    }
    return (
      (this.userInfo?.roles || []).includes(RoleName.AwardNominationReviewer) ||
      (this.userInfo?.roles || []).includes(RoleName.CountryNominationReviewer) ||
      (this.userInfo?.roles || []).includes(RoleName.NominationReviewer)
    );
  }

  @computed
  get isGroupLead(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.GroupLead;
    }
    return (this.userInfo?.roles || []).includes(RoleName.GroupLead);
  }

  @computed
  get isAwardLead(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.AwardLead;
    }
    return (this.userInfo?.roles || []).includes(RoleName.AwardLead);
  }

  @computed
  get isAwardNominationReviewer(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.AwardNominationReviewer;
    }
    return (this.userInfo?.roles || []).includes(RoleName.AwardNominationReviewer);
  }

  @computed
  get isRegionLead(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.RegionLead;
    }
    return (this.userInfo?.roles || []).includes(RoleName.RegionLead);
  }

  @computed
  get isCountryLead(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.CountryLead;
    }
    return (this.userInfo?.roles || []).includes(RoleName.CountryLead);
  }

  @computed
  get isCountryNominationReviewer(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.CountryNominationReviewer;
    }
    return (this.userInfo?.roles || []).includes(RoleName.CountryNominationReviewer);
  }

  @computed
  get isJudge(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.Judge;
    }
    return (this.userInfo?.roles || []).includes(RoleName.Judge);
  }

  @computed
  get isNominationJudge(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.NominationJudge;
    }
    return (this.userInfo?.roles || []).includes(RoleName.NominationJudge);
  }

  @computed
  get isPartner(): boolean {
    if (isPpeEnvironment && this.selectedRole) {
      return this.selectedRole === RoleName.Partner;
    }
    return (this.userInfo?.roles || []).includes(RoleName.Partner);
  }

  @computed
  get profileDisplayName(): string | undefined {
    return this.roles.find((role) => role.typeCode === this.selectedRole)?.displayName;
  }

  @computed
  get isUserImpersonated(): boolean {
    return isPpeEnvironment && !!this.selectedRole && !!this.profileDisplayName;
  }

  @computed
  get displayName(): string {
    if (this.userInfo?.firstName) return this.userInfo.firstName;
    if (this.userInfo?.lastName) return this.userInfo.lastName;
    return this.userInfo?.email ?? '';
  }

  @action
  setRoles(roles: UserRole[]) {
    this.roles = roles;
  }

  @action
  setSelectedRole(selectedRole: RoleName | undefined) {
    this.selectedRole = selectedRole;
  }

  setLastUpdateUserInfo(username: string) {
    localStorage.setItem(`${username}-lastsync`, new Date().toISOString());
  }

  removeLastUpdateUserInfo(username: string) {
    localStorage.removeItem(`${username}-lastsync`);
  }

  needUpdateUserInfo(username: string): boolean {
    const lastUpdateUserInfoString = localStorage.getItem(`${username}-lastsync`);
    const lastUpdateUserInfo = lastUpdateUserInfoString && new Date(lastUpdateUserInfoString);
    if (!lastUpdateUserInfo) return true;
    return lastUpdateUserInfo < subHours(new Date(), UPDATE_USERINFO_INTERVAL_IN_HOURS);
  }
}
